/**
 * MATLAB Parser
 * Transforms MATLAB tokens into an Abstract Syntax Tree (AST)
 */
import { MatlabTokenType, MatlabParseError } from "./types";
import { matlabTokenize } from "./lexer";
// Commands that MATLAB scripts conventionally invoke with command syntax
// (`hold on`) rather than function syntax (`hold('on')`).
const COMMAND_SYNTAX_FUNCTIONS = new Set([
    "axis",
    "box",
    "clc",
    "clear",
    "clearvars",
    "close",
    "colormap",
    "diary",
    "drawnow",
    "figure",
    "format",
    "grid",
    "hold",
    "more",
    "shading",
    "warning",
]);
export class MatlabParser {
    tokens;
    pos = 0;
    constructor(tokens) {
        this.tokens = tokens;
    }
    parse() {
        const statements = [];
        this.skipNewlines();
        while (!this.isAtEnd()) {
            const startLine = this.current().line;
            const stmt = this.parseStatement();
            if (stmt) {
                stmt.sourceLine = startLine;
                // Check if statement is terminated by semicolon (suppress output)
                if (this.check(MatlabTokenType.SEMICOLON)) {
                    stmt.suppressOutput = true;
                }
                statements.push(stmt);
            }
            this.skipNewlines();
        }
        if (statements.length === 1) {
            return statements[0];
        }
        return { type: "Block", statements };
    }
    current() {
        return (this.tokens[this.pos] || {
            type: MatlabTokenType.EOF,
            value: "",
            line: 0,
            column: 0,
        });
    }
    advance() {
        const token = this.current();
        if (!this.isAtEnd())
            this.pos++;
        return token;
    }
    isAtEnd() {
        return this.current().type === MatlabTokenType.EOF;
    }
    check(type) {
        return this.current().type === type;
    }
    match(...types) {
        for (const type of types) {
            if (this.check(type)) {
                this.advance();
                return true;
            }
        }
        return false;
    }
    expect(type, message) {
        if (this.check(type)) {
            return this.advance();
        }
        throw new MatlabParseError(`${message}, got ${this.current().type}`, {
            line: this.current().line,
            column: this.current().column,
        });
    }
    skipNewlines() {
        while (this.match(MatlabTokenType.NEWLINE, MatlabTokenType.SEMICOLON)) {
            // skip
        }
    }
    skipToEndOfStatement() {
        while (!this.isAtEnd() &&
            !this.check(MatlabTokenType.NEWLINE) &&
            !this.check(MatlabTokenType.SEMICOLON)) {
            this.advance();
        }
        this.skipNewlines();
    }
    parseStatement() {
        this.skipNewlines();
        if (this.check(MatlabTokenType.FUNCTION)) {
            return this.parseFunctionDeclaration();
        }
        if (this.check(MatlabTokenType.IF)) {
            return this.parseIfStatement();
        }
        if (this.check(MatlabTokenType.FOR)) {
            return this.parseForLoop();
        }
        if (this.check(MatlabTokenType.WHILE)) {
            return this.parseWhileLoop();
        }
        if (this.check(MatlabTokenType.SWITCH)) {
            return this.parseSwitchStatement();
        }
        if (this.check(MatlabTokenType.TRY)) {
            return this.parseTryStatement();
        }
        if (this.check(MatlabTokenType.RETURN)) {
            this.advance();
            return { type: "ReturnStatement" };
        }
        if (this.check(MatlabTokenType.BREAK)) {
            this.advance();
            return { type: "BreakStatement" };
        }
        if (this.check(MatlabTokenType.CONTINUE)) {
            this.advance();
            return { type: "ContinueStatement" };
        }
        if (this.check(MatlabTokenType.GLOBAL) ||
            this.check(MatlabTokenType.PERSISTENT)) {
            // Skip global/persistent declarations for now
            this.skipToEndOfStatement();
            return null;
        }
        const command = this.tryParseCommandSyntax();
        if (command)
            return command;
        return this.parseAssignmentOrExpression();
    }
    // MATLAB command syntax: `hold on` means hold('on'), `close all` means
    // close('all'). The general rule is ambiguous with indexing and arithmetic,
    // so it is applied only to the commands that are conventionally written
    // this way, and only when every remaining word on the line is a bare word.
    tryParseCommandSyntax() {
        if (!this.check(MatlabTokenType.IDENTIFIER))
            return null;
        if (!COMMAND_SYNTAX_FUNCTIONS.has(this.current().value)) {
            return null;
        }
        // Scan ahead: the words must run to the end of the statement, and there
        // must be at least one. Anything else (an operator, a paren, an '=') means
        // this is an ordinary expression, e.g. `axis = 3` or `hold(state)`.
        let lookahead = this.pos + 1;
        const words = [];
        while (lookahead < this.tokens.length) {
            const token = this.tokens[lookahead];
            if (token.type === MatlabTokenType.NEWLINE ||
                token.type === MatlabTokenType.SEMICOLON ||
                token.type === MatlabTokenType.EOF) {
                break;
            }
            if (token.type !== MatlabTokenType.IDENTIFIER &&
                token.type !== MatlabTokenType.STRING &&
                token.type !== MatlabTokenType.NUMBER) {
                return null;
            }
            words.push(String(token.value));
            lookahead++;
        }
        if (words.length === 0)
            return null;
        const name = this.advance().value;
        while (this.pos < lookahead)
            this.advance();
        return {
            type: "FunctionCall",
            name,
            args: words.map((word) => ({
                type: "StringLiteral",
                value: word,
            })),
        };
    }
    parseFunctionDeclaration() {
        this.advance(); // consume 'function'
        let outputs = [];
        let name;
        // Check for output arguments: [a, b] = funcname(...) or a = funcname(...)
        if (this.check(MatlabTokenType.LBRACKET)) {
            this.advance(); // consume '['
            while (!this.check(MatlabTokenType.RBRACKET) && !this.isAtEnd()) {
                if (this.check(MatlabTokenType.IDENTIFIER)) {
                    outputs.push(this.advance().value);
                }
                this.match(MatlabTokenType.COMMA);
            }
            this.expect(MatlabTokenType.RBRACKET, "Expected ']'");
            this.expect(MatlabTokenType.ASSIGN, "Expected '=' after output arguments");
            name = this.expect(MatlabTokenType.IDENTIFIER, "Expected function name")
                .value;
        }
        else if (this.check(MatlabTokenType.IDENTIFIER)) {
            // Could be: name(...) or output = name(...)
            const firstId = this.advance().value;
            if (this.match(MatlabTokenType.ASSIGN)) {
                outputs = [firstId];
                name = this.expect(MatlabTokenType.IDENTIFIER, "Expected function name")
                    .value;
            }
            else {
                name = firstId;
            }
        }
        else {
            throw new MatlabParseError("Expected function name", {
                line: this.current().line,
                column: this.current().column,
            });
        }
        // Parse parameters
        const params = [];
        if (this.match(MatlabTokenType.LPAREN)) {
            while (!this.check(MatlabTokenType.RPAREN) && !this.isAtEnd()) {
                if (this.check(MatlabTokenType.IDENTIFIER)) {
                    params.push(this.advance().value);
                }
                if (!this.check(MatlabTokenType.RPAREN)) {
                    this.match(MatlabTokenType.COMMA);
                }
            }
            this.expect(MatlabTokenType.RPAREN, "Expected ')' after parameters");
        }
        this.skipNewlines();
        // Parse function body until 'end'
        const body = this.parseBlock([MatlabTokenType.END]);
        this.expect(MatlabTokenType.END, "Expected 'end' to close function");
        return {
            type: "FunctionDeclaration",
            name,
            params,
            outputs,
            body,
        };
    }
    parseIfStatement() {
        this.advance(); // consume 'if'
        const condition = this.parseExpression();
        this.skipNewlines();
        const thenBranch = this.parseBlock([
            MatlabTokenType.ELSEIF,
            MatlabTokenType.ELSE,
            MatlabTokenType.END,
        ]);
        const elseifBranches = [];
        while (this.match(MatlabTokenType.ELSEIF)) {
            const elifCondition = this.parseExpression();
            this.skipNewlines();
            const elifBody = this.parseBlock([
                MatlabTokenType.ELSEIF,
                MatlabTokenType.ELSE,
                MatlabTokenType.END,
            ]);
            elseifBranches.push({ condition: elifCondition, body: elifBody });
        }
        let elseBranch;
        if (this.match(MatlabTokenType.ELSE)) {
            this.skipNewlines();
            elseBranch = this.parseBlock([MatlabTokenType.END]);
        }
        this.expect(MatlabTokenType.END, "Expected 'end' to close if statement");
        return {
            type: "IfStatement",
            condition,
            thenBranch,
            elseifBranches: elseifBranches.length > 0 ? elseifBranches : undefined,
            elseBranch,
        };
    }
    parseForLoop() {
        this.advance(); // consume 'for'
        const variable = this.expect(MatlabTokenType.IDENTIFIER, "Expected loop variable").value;
        this.expect(MatlabTokenType.ASSIGN, "Expected '=' after loop variable");
        const range = this.parseExpression();
        this.skipNewlines();
        const body = this.parseBlock([MatlabTokenType.END]);
        this.expect(MatlabTokenType.END, "Expected 'end' to close for loop");
        return {
            type: "ForLoop",
            variable,
            range,
            body,
        };
    }
    parseWhileLoop() {
        this.advance(); // consume 'while'
        const condition = this.parseExpression();
        this.skipNewlines();
        const body = this.parseBlock([MatlabTokenType.END]);
        this.expect(MatlabTokenType.END, "Expected 'end' to close while loop");
        return {
            type: "WhileLoop",
            condition,
            body,
        };
    }
    parseSwitchStatement() {
        this.advance(); // consume 'switch'
        const expression = this.parseExpression();
        this.skipNewlines();
        const cases = [];
        let otherwise;
        while (!this.check(MatlabTokenType.END) && !this.isAtEnd()) {
            if (this.match(MatlabTokenType.CASE)) {
                const values = [];
                // Case can have cell array of values: case {1, 2, 3}
                if (this.check(MatlabTokenType.LBRACE)) {
                    this.advance();
                    while (!this.check(MatlabTokenType.RBRACE) && !this.isAtEnd()) {
                        values.push(this.parseExpression());
                        this.match(MatlabTokenType.COMMA);
                    }
                    this.expect(MatlabTokenType.RBRACE, "Expected '}'");
                }
                else {
                    values.push(this.parseExpression());
                }
                this.skipNewlines();
                const body = this.parseBlock([
                    MatlabTokenType.CASE,
                    MatlabTokenType.OTHERWISE,
                    MatlabTokenType.END,
                ]);
                cases.push({ values, body });
            }
            else if (this.match(MatlabTokenType.OTHERWISE)) {
                this.skipNewlines();
                otherwise = this.parseBlock([MatlabTokenType.END]);
            }
            else {
                break;
            }
        }
        this.expect(MatlabTokenType.END, "Expected 'end' to close switch statement");
        return {
            type: "SwitchStatement",
            expression,
            cases,
            otherwise,
        };
    }
    parseTryStatement() {
        this.advance(); // consume 'try'
        this.skipNewlines();
        const tryBlock = this.parseBlock([
            MatlabTokenType.CATCH,
            MatlabTokenType.END,
        ]);
        let catchBlock;
        let catchVar;
        if (this.match(MatlabTokenType.CATCH)) {
            if (this.check(MatlabTokenType.IDENTIFIER)) {
                catchVar = this.advance().value;
            }
            this.skipNewlines();
            catchBlock = this.parseBlock([MatlabTokenType.END]);
        }
        this.expect(MatlabTokenType.END, "Expected 'end' to close try statement");
        return {
            type: "TryStatement",
            tryBlock,
            catchBlock,
            catchVar,
        };
    }
    parseBlock(terminators) {
        const statements = [];
        while (!this.isAtEnd() && !terminators.some((t) => this.check(t))) {
            const stmt = this.parseStatement();
            if (stmt) {
                statements.push(stmt);
            }
            this.skipNewlines();
        }
        return { type: "Block", statements };
    }
    parseAssignmentOrExpression() {
        // Check for multi-output assignment: [a, b] = func(...)
        if (this.check(MatlabTokenType.LBRACKET)) {
            const startPos = this.pos;
            this.advance(); // consume '['
            const targets = [];
            let isMultiAssignment = true;
            while (!this.check(MatlabTokenType.RBRACKET) && !this.isAtEnd()) {
                if (this.check(MatlabTokenType.IDENTIFIER)) {
                    targets.push({
                        type: "Identifier",
                        name: this.advance().value,
                    });
                }
                else if (this.check(MatlabTokenType.NOT)) {
                    // ~,b = func() syntax for ignoring outputs
                    this.advance();
                    targets.push({ type: "Identifier", name: "~" });
                }
                else if (!this.check(MatlabTokenType.COMMA)) {
                    // If we see something other than identifier, ~, or comma, it's not a multi-assignment
                    isMultiAssignment = false;
                    break;
                }
                this.match(MatlabTokenType.COMMA);
            }
            if (isMultiAssignment &&
                this.match(MatlabTokenType.RBRACKET) &&
                this.match(MatlabTokenType.ASSIGN)) {
                const value = this.parseExpression();
                return {
                    type: "MultiAssignment",
                    targets,
                    value,
                };
            }
            // Not a multi-assignment, backtrack and parse as array
            this.pos = startPos;
        }
        const expr = this.parseExpression();
        // Check for assignment
        if (this.match(MatlabTokenType.ASSIGN)) {
            // If we're at EOF after '=', treat it as a trailing '=' (calculator-style evaluation)
            // and just return the expression - ignore the trailing '='
            if (this.isAtEnd()) {
                return expr;
            }
            const value = this.parseExpression();
            // In MATLAB, arr(1) = x and func(1) use same syntax - we treat FunctionCall-like
            // patterns as IndexAccess for assignment purposes
            if (expr.type === "FunctionCall") {
                // Convert FunctionCall to IndexAccess for assignment
                return {
                    type: "Assignment",
                    target: {
                        type: "IndexAccess",
                        object: { type: "Identifier", name: expr.name },
                        indices: expr.args,
                    },
                    value,
                };
            }
            if (expr.type === "Identifier" ||
                expr.type === "IndexAccess" ||
                expr.type === "StructAccess") {
                return {
                    type: "Assignment",
                    target: expr,
                    value,
                };
            }
            throw new MatlabParseError("Invalid assignment target", {
                line: this.current().line,
                column: this.current().column,
            });
        }
        return expr;
    }
    parseExpression() {
        return this.parseOr();
    }
    parseOr() {
        let left = this.parseAnd();
        while (this.match(MatlabTokenType.OR, MatlabTokenType.SHORT_OR)) {
            const operator = this.tokens[this.pos - 1].value;
            const right = this.parseAnd();
            left = { type: "BinaryOp", operator, left, right };
        }
        return left;
    }
    parseAnd() {
        let left = this.parseEquality();
        while (this.match(MatlabTokenType.AND, MatlabTokenType.SHORT_AND)) {
            const operator = this.tokens[this.pos - 1].value;
            const right = this.parseEquality();
            left = { type: "BinaryOp", operator, left, right };
        }
        return left;
    }
    parseEquality() {
        let left = this.parseComparison();
        while (this.match(MatlabTokenType.EQ, MatlabTokenType.NE)) {
            const operator = this.tokens[this.pos - 1].value;
            const right = this.parseComparison();
            left = { type: "BinaryOp", operator, left, right };
        }
        return left;
    }
    parseComparison() {
        let left = this.parseColon();
        while (this.match(MatlabTokenType.LT, MatlabTokenType.LE, MatlabTokenType.GT, MatlabTokenType.GE)) {
            const operator = this.tokens[this.pos - 1].value;
            const right = this.parseColon();
            left = { type: "BinaryOp", operator, left, right };
        }
        return left;
    }
    parseColon() {
        let left = this.parseAddition();
        if (this.match(MatlabTokenType.COLON)) {
            const second = this.parseAddition();
            if (this.match(MatlabTokenType.COLON)) {
                const third = this.parseAddition();
                // a:b:c means start:step:end
                return { type: "RangeExpr", start: left, step: second, end: third };
            }
            // a:b means start:end
            return { type: "RangeExpr", start: left, end: second };
        }
        return left;
    }
    parseAddition() {
        let left = this.parseMultiplication();
        while (this.match(MatlabTokenType.PLUS, MatlabTokenType.MINUS)) {
            const operator = this.tokens[this.pos - 1].value;
            const right = this.parseMultiplication();
            left = { type: "BinaryOp", operator, left, right };
        }
        return left;
    }
    parseMultiplication() {
        let left = this.parsePower();
        while (this.match(MatlabTokenType.MULTIPLY, MatlabTokenType.DIVIDE, MatlabTokenType.BACKSLASH, MatlabTokenType.DOT_MULTIPLY, MatlabTokenType.DOT_DIVIDE, MatlabTokenType.DOT_BACKSLASH)) {
            const operator = this.tokens[this.pos - 1].value;
            const right = this.parsePower();
            left = { type: "BinaryOp", operator, left, right };
        }
        return left;
    }
    parsePower() {
        let left = this.parseUnary();
        while (this.match(MatlabTokenType.POWER, MatlabTokenType.DOT_POWER)) {
            const operator = this.tokens[this.pos - 1].value;
            const right = this.parseUnary();
            left = { type: "BinaryOp", operator, left, right };
        }
        return left;
    }
    parseUnary() {
        if (this.match(MatlabTokenType.MINUS)) {
            const operand = this.parseUnary();
            return { type: "UnaryOp", operator: "-", operand };
        }
        if (this.match(MatlabTokenType.PLUS)) {
            return this.parseUnary();
        }
        if (this.match(MatlabTokenType.NOT)) {
            const operand = this.parseUnary();
            return { type: "UnaryOp", operator: "~", operand };
        }
        return this.parsePostfix();
    }
    parsePostfix() {
        let expr = this.parsePrimary();
        while (true) {
            if (this.match(MatlabTokenType.TRANSPOSE)) {
                expr = { type: "UnaryOp", operator: "'", operand: expr, postfix: true };
            }
            else if (this.match(MatlabTokenType.CTRANSPOSE)) {
                expr = {
                    type: "UnaryOp",
                    operator: ".'",
                    operand: expr,
                    postfix: true,
                };
            }
            else if (this.match(MatlabTokenType.LPAREN)) {
                // Function call or indexing
                const args = this.parseArgList(MatlabTokenType.RPAREN);
                this.expect(MatlabTokenType.RPAREN, "Expected ')'");
                if (expr.type === "Identifier") {
                    // Could be function call or array indexing
                    expr = { type: "FunctionCall", name: expr.name, args };
                }
                else {
                    expr = { type: "IndexAccess", object: expr, indices: args };
                }
            }
            else if (this.match(MatlabTokenType.LBRACE)) {
                // Cell array indexing
                const indices = this.parseArgList(MatlabTokenType.RBRACE);
                this.expect(MatlabTokenType.RBRACE, "Expected '}'");
                expr = {
                    type: "IndexAccess",
                    object: expr,
                    indices,
                    isCellAccess: true,
                };
            }
            else if (this.match(MatlabTokenType.DOT)) {
                // Struct field access
                const field = this.expect(MatlabTokenType.IDENTIFIER, "Expected field name").value;
                expr = { type: "StructAccess", object: expr, field };
            }
            else {
                break;
            }
        }
        return expr;
    }
    parsePrimary() {
        // Number
        if (this.check(MatlabTokenType.NUMBER)) {
            const token = this.advance();
            return { type: "NumberLiteral", value: token.value };
        }
        // Imaginary number
        if (this.check(MatlabTokenType.IMAGINARY)) {
            const token = this.advance();
            return {
                type: "NumberLiteral",
                value: token.value,
                isImaginary: true,
            };
        }
        // String
        if (this.check(MatlabTokenType.STRING)) {
            const token = this.advance();
            return {
                type: "StringLiteral",
                value: token.value,
                quote: token.quote,
            };
        }
        // Identifier
        if (this.check(MatlabTokenType.IDENTIFIER)) {
            const token = this.advance();
            return { type: "Identifier", name: token.value };
        }
        // Function handle @
        if (this.match(MatlabTokenType.AT)) {
            if (this.check(MatlabTokenType.LPAREN)) {
                // Anonymous function: @(x) x^2
                return this.parseAnonymousFunction();
            }
            else {
                // Function handle: @sin
                const name = this.expect(MatlabTokenType.IDENTIFIER, "Expected function name").value;
                return { type: "FunctionHandle", name };
            }
        }
        // Parenthesized expression
        if (this.match(MatlabTokenType.LPAREN)) {
            const expr = this.parseExpression();
            this.expect(MatlabTokenType.RPAREN, "Expected ')' after expression");
            return expr;
        }
        // Array or matrix literal [...]
        if (this.match(MatlabTokenType.LBRACKET)) {
            return this.parseArrayLiteral();
        }
        // Cell array literal {...}
        if (this.match(MatlabTokenType.LBRACE)) {
            return this.parseCellArray();
        }
        // Colon operator by itself (used in indexing, e.g., A(:,1))
        if (this.check(MatlabTokenType.COLON)) {
            this.advance();
            return { type: "Identifier", name: ":" };
        }
        // 'end' keyword used as index (e.g., A(end), A(1:end))
        if (this.check(MatlabTokenType.END)) {
            this.advance();
            return { type: "Identifier", name: "end" };
        }
        throw new MatlabParseError(`Unexpected token: ${this.current().type}`, {
            line: this.current().line,
            column: this.current().column,
        });
    }
    parseAnonymousFunction() {
        this.expect(MatlabTokenType.LPAREN, "Expected '(' for anonymous function");
        const params = [];
        while (!this.check(MatlabTokenType.RPAREN) && !this.isAtEnd()) {
            if (this.check(MatlabTokenType.IDENTIFIER)) {
                params.push(this.advance().value);
            }
            if (!this.check(MatlabTokenType.RPAREN)) {
                this.match(MatlabTokenType.COMMA);
            }
        }
        this.expect(MatlabTokenType.RPAREN, "Expected ')' after parameters");
        const body = this.parseExpression();
        return { type: "AnonymousFunction", params, body };
    }
    parseArrayLiteral() {
        const rows = [];
        let currentRow = [];
        while (!this.check(MatlabTokenType.RBRACKET) && !this.isAtEnd()) {
            // Skip any leading newlines/semicolons
            if (this.check(MatlabTokenType.NEWLINE) ||
                this.check(MatlabTokenType.SEMICOLON)) {
                if (currentRow.length > 0) {
                    rows.push(currentRow);
                    currentRow = [];
                }
                this.advance();
                continue;
            }
            currentRow.push(this.parseExpression());
            // Comma or space separates columns
            if (this.match(MatlabTokenType.COMMA)) {
                continue;
            }
            // Semicolon or newline separates rows
            if (this.check(MatlabTokenType.SEMICOLON) ||
                this.check(MatlabTokenType.NEWLINE)) {
                if (currentRow.length > 0) {
                    rows.push(currentRow);
                    currentRow = [];
                }
                this.advance();
                continue;
            }
            // End of array
            if (this.check(MatlabTokenType.RBRACKET)) {
                break;
            }
        }
        if (currentRow.length > 0) {
            rows.push(currentRow);
        }
        this.expect(MatlabTokenType.RBRACKET, "Expected ']' to close array");
        // If single row, simplify to 1D array
        return { type: "ArrayLiteral", rows };
    }
    parseCellArray() {
        const rows = [];
        let currentRow = [];
        while (!this.check(MatlabTokenType.RBRACE) && !this.isAtEnd()) {
            if (this.check(MatlabTokenType.NEWLINE) ||
                this.check(MatlabTokenType.SEMICOLON)) {
                if (currentRow.length > 0) {
                    rows.push(currentRow);
                    currentRow = [];
                }
                this.advance();
                continue;
            }
            currentRow.push(this.parseExpression());
            if (this.match(MatlabTokenType.COMMA)) {
                continue;
            }
            if (this.check(MatlabTokenType.SEMICOLON) ||
                this.check(MatlabTokenType.NEWLINE)) {
                if (currentRow.length > 0) {
                    rows.push(currentRow);
                    currentRow = [];
                }
                this.advance();
                continue;
            }
            if (this.check(MatlabTokenType.RBRACE)) {
                break;
            }
        }
        if (currentRow.length > 0) {
            rows.push(currentRow);
        }
        this.expect(MatlabTokenType.RBRACE, "Expected '}' to close cell array");
        return { type: "CellArray", rows };
    }
    parseArgList(terminator) {
        const args = [];
        while (!this.check(terminator) && !this.isAtEnd()) {
            args.push(this.parseExpression());
            if (!this.check(terminator)) {
                this.match(MatlabTokenType.COMMA);
            }
        }
        return args;
    }
}
/**
 * Convenience function to parse MATLAB code
 */
export function matlabParse(input) {
    const tokens = matlabTokenize(input);
    const parser = new MatlabParser(tokens);
    return parser.parse();
}
//# sourceMappingURL=parser.js.map